import type { CrisisManagementFrameworkResult } from '../types.js';
import { S13_INCIDENT_TYPES, S13_PRIORITIES } from './s13Workflow.js';
import { s13ScenarioTrigger } from './s13Report.js';

export type S13ChecklistSection = 'matrix' | 'cards' | 'contacts' | 'legal';
export type S13ChecklistItem = { id: string; section: S13ChecklistSection; title: string;
    detail: string; token: string; incidentType?: string };

const text = (value: unknown) => String(value ?? '').trim();
const item = (id: string, section: S13ChecklistSection, title: string, detail: string,
    incidentType?: string): S13ChecklistItem => {
    let hash = 2166136261;
    const content = `${id}\0${detail}`;
    for (let index = 0; index < content.length; index++)
        hash = Math.imul(hash ^ content.charCodeAt(index), 16777619);
    return { id, section, title, detail, token: `${id}@${(hash >>> 0).toString(16).padStart(8, '0')}`,
        ...(incidentType ? { incidentType } : {}) };
};

export function s13ChecklistItems(report: CrisisManagementFrameworkResult): S13ChecklistItem[] {
    const items: S13ChecklistItem[] = [];
    for (let rowIndex = 0; rowIndex < S13_INCIDENT_TYPES.length; rowIndex++) {
        const incidentType = S13_INCIDENT_TYPES[rowIndex];
        const row = report.incidentClassificationMatrix.find(value => value.incidentType === incidentType);
        for (const priority of S13_PRIORITIES) {
            const cell = row?.levels.find(value => value.priority === priority);
            const rule = report.priorityRules.find(value => value.priority === priority);
            const trigger = s13ScenarioTrigger(cell?.escalationTrigger || '', incidentType, priority,
                report.priorityRules, report.incidentClassificationMatrix);
            items.push(item(`matrix:${rowIndex}:${priority}`, 'matrix', priority,
                `Client rule: ${text(rule?.trigger) || 'Needs verification'}\nTrigger: ${trigger}\n`+
                `Respond: ${cell?.responseMinutes ?? '—'} min\nAuthority: ${text(cell?.commandAuthority) || 'Needs verification'}\n`+
                `Notify: ${text(cell?.notificationRequirements) || 'Needs verification'}`, incidentType));
        }
    }
    for (let index = 0; index < S13_INCIDENT_TYPES.length; index++) {
        const incidentType = S13_INCIDENT_TYPES[index];
        const card = report.responseCards.find(value => value.incidentType === incidentType);
        items.push(item(`card:${index}`, 'cards', incidentType,
            `Immediate actions: ${text(card?.immediateActions) || 'Needs verification'}\n`+
            `Escalation: ${text(card?.escalation) || 'Needs verification'}\n`+
            `Transition: ${text(card?.transition) || 'Needs verification'}`));
    }
    const contacts: Array<[string, string, unknown]> = [
        ['director', 'Incident director', report.commandTeam?.incidentDirector],
        ['operations', 'Operations lead', report.commandTeam?.operationsLead],
        ['safety', 'Safety lead', report.commandTeam?.safetyLead],
        ['communications', 'Communications lead', report.commandTeam?.communicationsLead],
        ['coordinators', 'Site coordinators', report.commandTeam?.siteCoordinators],
        ['spokesperson', 'External spokesperson', report.externalSpokesperson],
        ['suppliers', 'Supplier contacts', report.supplierContacts],
        ['emergency', 'Police and fire contacts', report.policeFireContacts],
        ['directory', 'Contact directory', report.contactDirectory],
    ];
    for (const [id, title, value] of contacts)
        items.push(item(`contact:${id}`, 'contacts', title, text(value) || 'Needs verification'));
    items.push(item('legal:notifications', 'legal', 'Insurance notification decision',
        text(report.insuranceNotifications) || 'Needs verification'));
    items.push(item('legal:review', 'legal', 'Legal and insurance review',
        text(report.legalInsurance) || 'Needs verification'));
    return items;
}

export function cleanS13ChecklistReviewed(source: unknown, report: CrisisManagementFrameworkResult): string[] {
    if (!Array.isArray(source))
        return [];
    const valid = new Set(s13ChecklistItems(report).map(value => value.token));
    return [...new Set(source.filter((value): value is string => typeof value === 'string' && valid.has(value)))];
}
